<script setup lang="ts">
import {computed, inject} from "vue";
import {DropDownList} from "@progress/kendo-vue-dropdowns";
import {useAppDataStore} from "@/stores/app-data";
import {useCharacterDataStore} from "@/stores/character-data";
import {useUxData} from "@/stores/ux-data.ts";
import {handleError} from "@/appdata/globals.ts";
import type {IntlService} from "@progress/kendo-vue-intl";
import type {CharacterSheetInterface} from "@/types/characters";
import type {SwadeSkillInterface} from "@/types/items";

const emit = defineEmits(['updated']);
const store = useAppDataStore();
const swade = useCharacterDataStore();
const ux = useUxData();
const kendo: IntlService = inject('kendo') as IntlService;

const character = defineModel<CharacterSheetInterface>('character');

// Binary variant: the world authored a languages build option but no language skill.
// LanguageSkillConfigurator's "No Language Skill" switch nulls the modification's element, and
// applyBuildOptionLanguageSkill declines to write buildOptions.languages.skill for a null
// element. With no skill to rank, a language is simply known or not known, so the picks live
// on additionalData.knownLanguages instead of becoming skill ranks.
const isBinary = computed(() =>
    !!swade.buildOptions?.languages && !swade.buildOptions.languages.skill);

const knownLanguages = computed<any[]>(() => character.value?.additionalData?.knownLanguages ?? []);

// One flag for "the language block is interactive right now". It gates the picker, the badge
// styling, the remove handler and the header wording, so those cannot drift apart. Deriving
// them from store.props.edit directly would be wrong during advances, when edit is still true.
const editable = computed(() =>
    store.props.edit
    && !!swade.buildOptions?.languages
    && !!character.value
    && !swade.hasActiveAdvances(character.value));

const showKnownList = computed(() => isBinary.value && knownLanguages.value.length > 0);

const headerLabel = computed(() => editable.value
    ? (ux.labels?.swade?.characterCreator?.actions?.addLanguage ?? 'Add Language')
    : (ux.labels?.swade?.tools?.buildStatusIndicator?.languages ?? 'Languages'));

const slotsLabel = (slotsUsed: number) => kendo.format(
    ux.labels?.swade?.characterCreator?.languages?.slotsUsedFormatString ?? '{0}',
    slotsUsed,
);

const addKnownLanguage = (event: any) => {
  // Guard exists to satisfy the type-checker: character.value is typed as possibly undefined,
  // but the sole caller onLanguageSelect already guarantees it is set before calling here.
  if (!character.value) return;

  // Deliberately no fullyAllocated gate: exceeding the language budget is allowed;
  // updateAllocatedLanguages sets overAllocated and the build status bar flags it.
  const selection = event.value;

  character.value.additionalData ??= {};
  if (!Array.isArray(character.value.additionalData.knownLanguages)) {
    character.value.additionalData.knownLanguages = [];
  }

  // Known or not known — a language cannot be known twice.
  if (character.value.additionalData.knownLanguages.some((item: any) => item.label === selection.label)) return;

  character.value.additionalData.knownLanguages.push({
    label: selection.label,
    slotsUsed: selection.slotsUsed ?? 1,
  });

  swade.updateAllocatedLanguages(character.value);
  emit('updated', event);
};

const addLanguageSkill = (event: any) => {
  // Guard exists to satisfy the type-checker: character.value is typed as possibly undefined,
  // but the sole caller onLanguageSelect already guarantees it is set before calling here.
  if (!character.value) return;

  if (!character.value.protoData) {
    character.value.protoData = swade.newProtoData();
  }
  else if (!Array.isArray(character.value.protoData.languages)) {
    character.value.protoData.languages = [];
  }

  if (!swade.build.languages?.fullyAllocated) {
    character.value.protoData.languages.push({ ...event.value })
  }

  store.retrieveElement({ uuid: swade.buildOptions.languages.skill })
      .then((skill: SwadeSkillInterface) => {
        let baseRank: string | null = "d6";
        // A pick past the budget was not pushed above and never takes the native d8, so the budget
        // decides first. Then paid entries only: a bonus language (slotsUsed 0, spec 2026-09-21)
        // landing first must not steal the native d8 from the player's first pick.
        const paidPicks = character.value.protoData.languages
            .filter((language: { slotsUsed?: number }) => (language.slotsUsed ?? 1) > 0).length;
        if (swade.build.languages?.fullyAllocated) {
          baseRank = null;
        }
        else if (paidPicks === 1) {
          baseRank = "d8";
        }

        return swade.addSkillToCharacter(character.value, skill, true, baseRank, event.value.label);
      })
      .then(() => {
        emit('updated', event);
      })
      .catch((error) => { handleError(error) })
};

const onLanguageSelect = (event: any) => {
  if (!character.value || !event.value) return;

  return isBinary.value ? addKnownLanguage(event) : addLanguageSkill(event);
};

const onRemoveKnownLanguage = (item: any) => {
  // Guarded here rather than in the template: the same v-for renders read-only in view mode
  // and during advances, and a second conditional on @click would duplicate this rule.
  if (!editable.value) return;

  const known = character.value?.additionalData?.knownLanguages;
  if (!Array.isArray(known)) return;

  // Splice in place rather than reassigning `knownLanguages`, so any computed reading it
  // through this defineModel-bound prop chain (a second hop once nested under Skills.vue's
  // own defineModel('character')) reactively re-evaluates — same reasoning as
  // character-data.ts removeItem: `array = newArray` produces the right data but doesn't
  // reliably re-trigger consumer computeds when the value flows through a defineModel chain.
  const idx = known.indexOf(item);
  if (idx >= 0) known.splice(idx, 1);

  swade.updateAllocatedLanguages(character.value);
  emit('updated', item);
};
</script>

<template>
  <template v-if="editable || showKnownList">
    <div class="list-group-item header skill-list-header">
      {{ headerLabel }}
    </div>
    <div v-if="showKnownList" class="list-group-item known-languages">
      <!-- Flex only while editable. In view mode `gap-1` would space the comma run out
           into "Common , Elvish", so the spans flow as plain inline text instead. -->
      <div :class="editable ? 'd-flex flex-fill flex-wrap gap-1' : ''">
        <!-- One loop for both modes, after Gear.vue:268-294: badge class, remove icon and
             trailing comma all toggle off `editable`. The run-on final line is deliberate and
             copies Gear.vue:284 — a newline before the comma span renders as a space. -->
        <span v-for="(language, key) in knownLanguages" :key="language.label"
              class="known-language"
              :class="editable ? 'btn badge bg-primary-subtle text-primary px-2' : ''"
              :role="editable ? 'button' : undefined"
              @click="onRemoveKnownLanguage(language)"
        >{{ language.label }}<span v-if="language.slotsUsed > 1"> ({{ slotsLabel(language.slotsUsed) }})</span><span v-if="editable" class="fa-light fa-fw fa-times ms-1"></span><span v-else-if="key < knownLanguages.length - 1">, </span></span>
      </div>
    </div>
    <div v-if="editable" class="list-group-item language-chooser finder">
      <DropDownList
          :value="null"
          text-field="label"
          :data-items="swade.buildOptions.languages.core"
          :item-render="'language-selection'"
          @change="onLanguageSelect"
      >
        <template #language-selection="{ props }">
          <li class="k-list-item" @click="(ev) => props.onClick(ev)">
            <span class="k-list-item-text">
            {{ props.dataItem.label }}
            <span v-if="props.dataItem.slotsUsed > 1">({{ slotsLabel(props.dataItem.slotsUsed) }})</span>
            </span>
          </li>
        </template>
      </DropDownList>
    </div>
  </template>
</template>
